import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getReglementsByUser } from "../../../services/achatsServices";
import { createCheckoutSessionForArticles } from "../../../services/paymentServices";
import PaymentSuccess from "./PaymentSuccess";
import { getSettings } from "../../../services/settingsServices";
import ArticleTable from "./ArticleTable";
import ArticleCard from "./ArticleCard";
import PaymentPlanModal from "./PaymentPlanModal";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import { ToastContainer, toast } from "react-toastify";
import formatDate from "../../../utils/formatDate";
import { FaBell } from "react-icons/fa";

const UsersReglements = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [reglements, setReglements] = useState([]);
  const [defaultPhotoUrl, setDefaultPhotoUrl] = useState("");
  const [maxPayments, setMaxPayments] = useState(5);
  const [paymentInterval, setPaymentInterval] = useState(1);

  const [sessionId, setSessionId] = useState(null);
  const [isPaymentPlanModalOpen, setIsPaymentPlanModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [payingId, setPayingId] = useState(null); // anti double-clic

  // Notifs
  const [notifyingId, setNotifyingId] = useState(null);
  const [manualNotifiedIds, setManualNotifiedIds] = useState([]);
  const notifiedIdsRef = useRef(new Set()); // mémorise ce qui a été notifié (session courante)
  const prevStatusRef = useRef(new Map()); // mémorise les anciens statuts "id -> statut"
  const autoNotifyNextLoadRef = useRef(false); // déclenche l'auto-notif au prochain load
  const autoNotifyLockRef = useRef(false); // verrou pour éviter doublons concurrents
  const [autoNotifyEnabled] = useState(true);

  // === INIT: charger l'historique de notifs depuis localStorage (dédup cross-reload) ===
  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("notifiedReglementIds") || "[]"
      );
      if (Array.isArray(saved)) {
        notifiedIdsRef.current = new Set(saved);
      }
    } catch {}
  }, []);

  const persistNotified = useCallback(() => {
    try {
      localStorage.setItem(
        "notifiedReglementIds",
        JSON.stringify(Array.from(notifiedIdsRef.current))
      );
    } catch {}
  }, []);

  // Settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        if (Array.isArray(settings) && settings.length > 0) {
          const s = settings[0];
          setDefaultPhotoUrl(s.photo_url_default_lic_cot || "");
          setMaxPayments(s.max_payments ?? 5);
          setPaymentInterval(s.payment_interval ?? 1);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres", error);
      }
    };
    fetchSettings();
  }, []);

  // Lecture du session_id dans l’URL (Stripe success)
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sid = query.get("session_id");
    if (sid) {
      setSessionId(sid);
      autoNotifyNextLoadRef.current = true; // on activera une seule auto-notif ciblée
    }
  }, []);

  // Après succès Stripe : refresh + nettoyage paramètre
  useEffect(() => {
    const refreshAfterSuccess = async () => {
      if (!sessionId) return;
      try {
        await loadReglements();
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      }
    };
    refreshAfterSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // loadReglements est stable via useCallback deps

  // === AUTO-NOTIFS ROBUSTES & DÉDUPLIQUÉES ===
  const checkAutoNotifications = useCallback(
    async (freshList) => {
      if (!autoNotifyEnabled || autoNotifyLockRef.current) return;

      // 1) Pas de retour Stripe : on mémorise juste les statuts pour détecter les transitions plus tard.
      if (!autoNotifyNextLoadRef.current) {
        freshList.forEach((r) =>
          prevStatusRef.current.set(r.id, r.statut_reglement)
        );
        return;
      }

      // 2) Au retour Stripe : NE PAS notifier tout ce qui est déjà "Réglé".
      //    On cible AU MAXIMUM UN SEUL règlement récent ou lié à la session.
      autoNotifyLockRef.current = true;

      try {
        const paid = freshList.filter(
          (r) =>
            r &&
            r.statut_reglement === "Réglé" &&
            !notifiedIdsRef.current.has(r.id)
        );

        if (paid.length === 0) {
          // Rien à notifier, on sort proprement
          return;
        }

        // Heuristique de sélection:
        // a) si l'objet possède un champ session pour faire le lien (ex: stripe_session_id), on privilégie celui-ci
        //    (à adapter si ton API renvoie un champ de session)
        let candidate =
          paid.find(
            (r) => r.stripe_session_id && r.stripe_session_id === sessionId
          ) || paid.find((r) => r.session_id && r.session_id === sessionId);

        // b) sinon, on prend un règlement "récent" (moins de 15 minutes)
        if (!candidate) {
          const recentPaid = paid
            .filter((r) => isRecent(r.updatedAt || r.date_achat, 15))
            .sort(
              (a, b) =>
                new Date(b.updatedAt || b.date_achat) -
                new Date(a.updatedAt || a.date_achat)
            );
          candidate = recentPaid[0];
        }

        // c) fallback ultime : le plus récent par date_achat
        if (!candidate) {
          candidate = paid.sort(
            (a, b) =>
              new Date(b.updatedAt || b.date_achat) -
              new Date(a.updatedAt || a.date_achat)
          )[0];
        }

        if (candidate) {
          try {
            const nomArticle = candidate.Article
              ? candidate.Article.nom
              : "Règlement Licence-Cotisation";
            const msg = `${user?.prenom || ""} ${
              user?.nom || ""
            } a effectué un paiement en ligne : ${nomArticle} – ${
              candidate.prix_total
            } € (achat du ${formatDate(candidate.date_achat)}).`;
            await sendNotificationToAdmin(msg, user?.token);
            notifiedIdsRef.current.add(candidate.id);
            persistNotified();
            toast.success("Notification envoyée aux administrateurs.");
          } catch (e) {
            console.error(e);
            toast.error("Erreur lors de l'envoi de la notification.");
          }
        }
      } finally {
        // MàJ des anciens statuts et reset des flags/lock
        freshList.forEach((r) =>
          prevStatusRef.current.set(r.id, r.statut_reglement)
        );
        autoNotifyNextLoadRef.current = false;
        autoNotifyLockRef.current = false;
      }
    },
    [autoNotifyEnabled, persistNotified, sessionId, user]
  );

  // Chargement règlements
  const loadReglements = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await getReglementsByUser(userId);
      if (!Array.isArray(response)) {
        setReglements([]);
        return;
      }
      const sorted = [...response].sort(
        (a, b) => new Date(b.date_achat) - new Date(a.date_achat)
      );
      const withExtras = sorted.map((r) => ({
        ...r,
        saison: determineSeason(r.date_achat),
        nomArticle: r.Article ? r.Article.nom : "Règlement Inscription",
        photoUrl: r.Article
          ? r.Article.photo_url || defaultPhotoUrl
          : defaultPhotoUrl,
      }));

      await checkAutoNotifications(withExtras);

      setReglements(withExtras);
    } catch (error) {
      console.error("Erreur lors de la récupération des règlements", error);
    }
  }, [userId, defaultPhotoUrl, checkAutoNotifications]);

  useEffect(() => {
    loadReglements();
  }, [loadReglements]);

  const nonPaidArticles = useMemo(
    () =>
      reglements.filter((r) =>
        ["Non Réglé", "En Attente"].includes(r.statut_reglement)
      ),
    [reglements]
  );

  // Helpers locaux
  const toNumberSafe = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const computeUnitPrice = (reg) => {
    const qty = Math.max(1, toNumberSafe(reg.quantite));
    const total = toNumberSafe(reg.prix_total);
    const unitFromReg = toNumberSafe(reg.prix_unitaire);
    const unitFromArticle = toNumberSafe(reg?.Article?.prix);

    // Ordre de priorité : prix_unitaire explicite > Article.prix > (prix_total / quantite)
    const unit = unitFromReg || unitFromArticle || (qty ? total / qty : total);

    // Évite les flottants chelous
    return Number(unit.toFixed(2));
  };

  // ⬇️ Remplace intégralement ta fonction par celle-ci
  const handlePayerClick = async (reglementId) => {
    if (payingId) return;
    try {
      const reg = reglements.find((r) => r.id === reglementId);
      if (!reg) return;
      if (reg.autorisation_achat === false) return;
      if (!["Non Réglé", "En Attente"].includes(reg.statut_reglement)) return;

      setPayingId(reglementId);

      const qty = Math.max(1, Number(reg.quantite) || 1);
      const unitPrice = computeUnitPrice(reg);

      // ⚠️ Si ton back attend des centimes, utilise Math.round(unitPrice * 100)
      const items = [
        {
          id: reg.id,
          name: reg.Article ? reg.Article.nom : "Règlement Inscription",
          price: unitPrice, // ⇦ prix UNITAIRE en euros
          quantity: qty, // ⇦ quantité réelle
        },
      ];

      // Petit garde-fou (log uniquement)
      const expectedTotal = Number((unitPrice * qty).toFixed(2));
      const frontTotal = Number((Number(reg.prix_total) || 0).toFixed(2));
      if (Math.abs(expectedTotal - frontTotal) > 0.02) {
        console.warn("[Paiement] Total recalculé ≠ total affiché :", {
          expectedTotal,
          frontTotal,
          reg,
        });
      }

      const url = await createCheckoutSessionForArticles(items);
      if (typeof url === "string" && url.startsWith("http")) {
        window.location.href = url;
      } else {
        console.error("URL de paiement invalide:", url);
      }
    } catch (error) {
      console.error("Erreur création session de paiement:", error);
    } finally {
      setPayingId(null);
    }
  };

  const handlePayerEnPlusieursFoisClick = (reglementId) => {
    const article = reglements.find((r) => r.id === reglementId);
    if (!article) return;
    setSelectedArticle(article);
    setIsPaymentPlanModalOpen(true);
  };

  const handlePaymentPlanSelection = (selectedPlan) => {
    console.log("Paiement en plusieurs fois sélectionné :", selectedPlan);
  };

  // Bouton "Prévenir l'admin"
  const handleNotifyAdmins = async (article) => {
    if (!article || notifyingId) return;
    // ✅ éviter double-notif manuelle si déjà notifié (auto ou manuel)
    if (
      notifiedIdsRef.current.has(article.id) ||
      manualNotifiedIds.includes(article.id)
    ) {
      toast.info("Cette notification a déjà été envoyée.");
      return;
    }
    try {
      setNotifyingId(article.id);
      const nomArticle = article.Article
        ? article.Article.nom
        : "Règlement Licence-Cotisation";
      const msg = `${user?.prenom || ""} ${
        user?.nom || ""
      } a confirmé son paiement en ligne : ${nomArticle} – ${
        article.prix_total
      } € (achat du ${formatDate(article.date_achat)}).`;
      await sendNotificationToAdmin(msg, user?.token);

      notifiedIdsRef.current.add(article.id);
      persistNotified();
      setManualNotifiedIds((ids) => [...new Set([...ids, article.id])]);

      toast.success("Notification envoyée aux administrateurs.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'envoi de la notification.");
    } finally {
      setNotifyingId(null);
    }
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Vos Commandes & Règlements"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau d'info */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col gap-2">
              <h4 className="text-lg sm:text-xl font-semibold">Règlements</h4>
              <p className="text-white/90 text-sm">
                Réglez en ligne, en toute sécurité, vos articles commandés en
                boutique et vos licences-cotisations.
              </p>
              <p className="text-white/90 text-sm">
                Vous avez la possibilité de régler en plusieurs échéances
                (Licence-Cotisation uniquement).
              </p>
              <p className="text-white/90 text-sm">
                Si le bouton de paiement n'est pas actif, c'est que le règlement
                a été désactivé par le bureau.
              </p>
              <p className="text-white/90 text-sm">
                Une fois le paiement effectué, vous pouvez notifier
                l'administrateur avec le bouton 🔔 Prévenir l'admin.
              </p>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Vue Table (desktop) */}
            <div className="w-full p-2 border-gray-300">
              <ArticleTable
                articles={reglements}
                getStatusColor={getStatusColor}
                defaultPhotoUrl={defaultPhotoUrl}
                handlePayerClick={handlePayerClick}
                handlePayerEnPlusieursFoisClick={
                  handlePayerEnPlusieursFoisClick
                }
                payingId={payingId}
                handleNotifyClick={handleNotifyAdmins}
                notifyingId={notifyingId}
                isAlreadyNotified={(id) =>
                  notifiedIdsRef.current.has(id) ||
                  manualNotifiedIds.includes(id)
                }
              />
            </div>

            {/* Vue Card (mobile) */}
            <div className="w-full p-2 lg:ml-4">
              <ArticleCard
                articles={reglements}
                getStatusColor={getStatusColor}
                defaultPhotoUrl={defaultPhotoUrl}
                handlePayerClick={handlePayerClick}
                handlePayerEnPlusieursFoisClick={
                  handlePayerEnPlusieursFoisClick
                }
                payingId={payingId}
                handleNotifyClick={handleNotifyAdmins}
                notifyingId={notifyingId}
                isAlreadyNotified={(id) =>
                  notifiedIdsRef.current.has(id) ||
                  manualNotifiedIds.includes(id)
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modale paiement fractionné */}
      {isPaymentPlanModalOpen && selectedArticle && (
        <PaymentPlanModal
          isOpen={isPaymentPlanModalOpen}
          onClose={() => setIsPaymentPlanModalOpen(false)}
          totalAmount={selectedArticle.prix_total}
          maxPayments={maxPayments}
          paymentInterval={paymentInterval}
          handlePaymentPlanSelection={handlePaymentPlanSelection}
          article={selectedArticle}
          refreshDataAfterUpdate={loadReglements}
        />
      )}

      {sessionId && <PaymentSuccess sessionId={sessionId} />}
      <ToastContainer />
    </section>
  );
};

export default UsersReglements;

// Helpers
function getStatusColor(status) {
  switch (status) {
    case "Non Réglé":
      return "bg-red-500 text-white";
    case "Réglé":
      return "bg-green-500 text-white";
    case "En Attente":
      return "bg-yellow-300 text-black";
    case "Annulé":
      return "bg-black text-white";
    case "Remboursé":
      return "bg-purple-500 text-white";
    case "Partiellement Réglé":
      return "bg-green-200 text-black";
    default:
      return "bg-gray-200 text-black";
  }
}

function determineSeason(dateString) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const seasonStart = new Date(`${year}-08-01`);
  const seasonEnd = new Date(`${year + 1}-07-31`);
  if (date >= seasonStart && date <= seasonEnd) {
    return `Saison ${year}-${year + 1}`;
  } else {
    return `Saison ${year - 1}-${year}`;
  }
}

// Considère "récent" si dans les N minutes passées
function isRecent(dateString, minutes = 15) {
  if (!dateString) return false;
  const t = new Date(dateString).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= minutes * 60 * 1000;
}
